import { useCallback, useContext } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { addToUserCart } from 'actions/UserCartActions'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { generateMerchandiseCartItem, generateMerchandiseCartItems } from 'lib/checkout/merchandiseCartItemUtils'
import { fillArray, isNonNullable } from 'lib/array/arrayUtils'
import { trackEvent } from 'analytics/analytics'
import {
  addToCart,
  addToCartWithContext,
  createMerchandiseItemContextByMerchandiseItem,
} from 'analytics/snowplow/events'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import ModalContext from 'contexts/ModalContext'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import { selectLoggedIn } from 'selectors/accountSelectors'

interface UseMerchandiseCartActionsOptions {
  onAfterAddToCart?: () => void
  onAfterBuyNow?: () => void
  isWeddingGiftRegistry?: boolean
  addItemsToWeddingGiftRegistryCart?: (items: Array<App.Checkout.AnyItem>) => boolean
}

interface UseMerchandiseCartActionsResult {
  onAddToCart: () => Promise<void>
  onBuyNow: () => Promise<void>
}

function useMerchandiseCartActions(
  offer: App.MerchandiseOffer,
  variant: App.MerchandiseOfferVariant,
  quantity: number,
  options?: UseMerchandiseCartActionsOptions,
): UseMerchandiseCartActionsResult {
  const dispatch = useAppDispatch()
  const loggedIn = useAppSelector(state => selectLoggedIn(state))
  const showModal = useContext(ModalContext)

  const {
    onAfterAddToCart,
    onAfterBuyNow,
    isWeddingGiftRegistry,
    addItemsToWeddingGiftRegistryCart,
  } = options ?? {}

  const onAddToCart = useCallback(async() => {
    if (!loggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal
        offerDetails={{
          title: offer.name,
          value: variant.price,
          image: offer.images?.[0],
          imageTitle: offer.name,
          merchandiseVariant: variant,
          quantity,
        }}
      />)
      if (!result.loggedIn) {
        return
      }
    }

    const merchandiseItems = fillArray(quantity).map(() => generateMerchandiseCartItem(offer, variant))
    if (isWeddingGiftRegistry && addItemsToWeddingGiftRegistryCart) {
      addItemsToWeddingGiftRegistryCart(merchandiseItems)
      const contextItems = merchandiseItems
        .map(item => createMerchandiseItemContextByMerchandiseItem(item))
        .filter(isNonNullable)
      trackEvent(addToCartWithContext(contextItems))
      onAfterAddToCart?.()
      return
    }

    dispatch(addToUserCart(merchandiseItems))
    const contextItems = merchandiseItems
      .map(item => createMerchandiseItemContextByMerchandiseItem(item))
      .filter(isNonNullable)
    trackEvent(addToCartWithContext(contextItems))
    onAfterAddToCart?.()
  }, [addItemsToWeddingGiftRegistryCart, dispatch, isWeddingGiftRegistry, offer, variant, quantity, showModal, loggedIn, onAfterAddToCart])

  const onBuyNow = useCallback(async() => {
    if (!loggedIn) {
      const result = await showModal<AccountAccessModalResult>(<AccountAccessModal
        offerDetails={{
          title: offer.name,
          value: variant.price,
          image: offer.images?.[0],
          imageTitle: offer.name,
          merchandiseVariant: variant,
          quantity,
        }}
      />)
      if (!result.loggedIn) {
        return
      }
    }
    const cartItems = generateMerchandiseCartItems(offer, variant, { quantity })

    dispatch(initialiseCheckout(cartItems))
    trackEvent(addToCart())
    dispatch(navigateToCheckout())
    onAfterBuyNow?.()
  }, [dispatch, offer, variant, quantity, showModal, loggedIn, onAfterBuyNow])

  return {
    onAddToCart,
    onBuyNow,
  }
}

export default useMerchandiseCartActions
